import React, { useState, useEffect } from 'react';
import { TimeEntry } from '../../types/task';
import Modal from '../common/Modal';
import DateTimePicker from '../common/DateTimePicker';
import { ClockIcon, CalendarIcon, CheckCircleIcon } from '@heroicons/react/24/outline';
import { useLanguage } from '../../contexts/LanguageContext';

interface TimeEntryFormProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: TimeEntryFormData) => Promise<void>;
  initialData?: TimeEntry;
  userName: string;
}

export interface TimeEntryFormData {
  description: string;
  startTime: Date;
  endTime: Date;
  isBillable: boolean;
}

export default function TimeEntryForm({ 
  isOpen, 
  onClose, 
  onSubmit, 
  initialData,
  userName 
}: TimeEntryFormProps) {
  const { t } = useLanguage();
  const [description, setDescription] = useState('');
  const [startDateTime, setStartDateTime] = useState<Date | undefined>(undefined);
  const [endDateTime, setEndDateTime] = useState<Date | undefined>(undefined);
  const [isBillable, setIsBillable] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (initialData) {
      setDescription(initialData.description || '');
      setIsBillable(initialData.isBillable ?? false);
      setStartDateTime(new Date(initialData.startTime));
      setEndDateTime(initialData.endTime ? new Date(initialData.endTime) : undefined);
    } else {
      // Alapértelmezett értékek (ma 9:00 - 17:00)
      const now = new Date();
      const start = new Date(now);
      start.setHours(9, 0, 0, 0);
      const end = new Date(now);
      end.setHours(17, 0, 0, 0);
      setStartDateTime(start);
      setEndDateTime(end);
      setIsBillable(false);
      setDescription('');
    }
  }, [initialData, isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Validáció
    if (!description.trim()) {
      setError(t('timeTracking.descriptionRequired'));
      return;
    }

    if (!startDateTime) {
      setError(t('timeTracking.startRequired') || 'Kezdés időpontja kötelező');
      return;
    }

    if (!endDateTime) {
      setError(t('timeTracking.endRequired') || 'Befejezés időpontja kötelező');
      return;
    }

    if (endDateTime <= startDateTime) {
      setError(t('timeTracking.endAfterStart') || 'A befejezés időpontja későbbi kell legyen, mint a kezdés');
      return;
    }

    const duration = (endDateTime.getTime() - startDateTime.getTime()) / 1000;
    if (duration > 24 * 3600) {
      setError(t('timeTracking.maxDuration') || 'Az időbejegyzés nem lehet hosszabb 24 óránál');
      return;
    }

    setLoading(true);
    try {
      await onSubmit({
        description,
        startTime: startDateTime,
        endTime: endDateTime,
        isBillable
      });
      onClose();
    } catch (err: any) {
      setError(err.message || 'Hiba történt a mentés során');
    } finally {
      setLoading(false);
    }
  };

  const calculateDuration = () => {
    if (!startDateTime || !endDateTime) return '00:00:00';
    
    if (endDateTime <= startDateTime) return '00:00:00';
    
    const seconds = Math.floor((endDateTime.getTime() - startDateTime.getTime()) / 1000);
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title=""
      size="lg"
      zIndex={70}
    >
      {/* Custom Header */}
      <div className="-m-6 mb-4 md:mb-6 px-3 md:px-6 py-3 md:py-5 bg-white dark:bg-gray-800 border-b-2 border-gray-100 dark:border-gray-700">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 md:gap-4 min-w-0 flex-1">
            <div className="p-1.5 md:p-2.5 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg md:rounded-xl shadow-lg flex-shrink-0">
              <ClockIcon className="h-4 w-4 md:h-6 md:w-6 text-white" />
            </div>
            <div className="min-w-0 flex-1">
              <h2 className="text-sm md:text-xl font-bold text-gray-900 dark:text-white truncate">
                {initialData ? t('timeTracking.editEntry') : t('timeTracking.newEntry')}
              </h2>
              <p className="text-xs md:text-sm text-gray-500 dark:text-gray-400 mt-0.5 truncate">
                {userName}
              </p>
            </div>
          </div>
          <div className="text-right hidden sm:block flex-shrink-0">
            <div className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
              {t('timeTracking.dateLabel')}
            </div>
            <div className="text-sm font-semibold text-gray-900 dark:text-white">
              {new Date().toLocaleDateString('hu-HU', { year: 'numeric', month: 'long', day: 'numeric' })}
            </div>
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-3 md:space-y-6">
        {error && (
          <div className="bg-red-50 dark:bg-red-900/20 border-l-4 border-red-500 rounded-r-lg p-2 md:p-4 shadow-sm">
            <p className="text-xs md:text-sm font-medium text-red-800 dark:text-red-200">{error}</p>
          </div>
        )}

        {/* Leírás */}
        <div className="space-y-1 md:space-y-2">
          <label className="flex items-center gap-1.5 md:gap-2 text-xs md:text-sm font-semibold text-gray-900 dark:text-gray-100">
            <ClockIcon className="h-3.5 w-3.5 md:h-4 md:w-4 text-blue-600 dark:text-blue-400" />
            <span>{t('timeTracking.description')}</span>
          </label>
          <input
            type="text"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder={t('timeTracking.descriptionPlaceholder')}
            className="w-full px-3 md:px-4 py-2 md:py-3 border-2 border-gray-200 dark:border-gray-600 rounded-lg md:rounded-xl bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:border-blue-500 dark:focus:border-blue-400 focus:ring-4 focus:ring-blue-500/20 dark:focus:ring-blue-400/20 transition-all text-sm"
          />
        </div>

        {/* Kezdés */}
        <div className="space-y-1 md:space-y-2">
          <label className="flex items-center gap-1.5 md:gap-2 text-xs md:text-sm font-semibold text-gray-900 dark:text-gray-100">
            <CalendarIcon className="h-3.5 w-3.5 md:h-4 md:w-4 text-green-600 dark:text-green-400" />
            <span>{t('timeTracking.startTime')} *</span>
          </label>
          <DateTimePicker
            value={startDateTime}
            onChange={setStartDateTime}
            showTime={true}
          />
        </div>

        {/* Befejezés */}
        <div className="space-y-1 md:space-y-2">
          <label className="flex items-center gap-1.5 md:gap-2 text-xs md:text-sm font-semibold text-gray-900 dark:text-gray-100">
            <CheckCircleIcon className="h-3.5 w-3.5 md:h-4 md:w-4 text-purple-600 dark:text-purple-400" />
            <span>{t('timeTracking.endTime')} *</span>
          </label>
          <DateTimePicker
            value={endDateTime}
            onChange={setEndDateTime}
            showTime={true}
            minDate={startDateTime}
          />
        </div>

        {/* Időtartam */}
        <div className="relative overflow-hidden bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 border-2 border-blue-200 dark:border-blue-700/50 rounded-xl md:rounded-2xl p-3 md:p-5 shadow-sm dark:shadow-blue-900/20">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-1.5 md:gap-2">
              <ClockIcon className="h-4 w-4 md:h-5 md:w-5 text-blue-600 dark:text-blue-400" />
              <span className="text-xs md:text-sm font-semibold text-blue-900 dark:text-blue-200">
                {t('timeTracking.duration')}
              </span>
            </div>
            <span className="text-xl md:text-3xl font-mono font-bold text-blue-600 dark:text-blue-300 tracking-tight">
              {calculateDuration()}
            </span>
          </div>
        </div>

        {/* Számlázható */}
        <div className="flex items-center gap-2 md:gap-3 p-2 md:p-4 bg-gray-50 dark:bg-gray-800/50 rounded-lg md:rounded-xl border-2 border-gray-200 dark:border-gray-700 hover:border-blue-300 dark:hover:border-blue-600 transition-all cursor-pointer">
          <input
            type="checkbox"
            id="isBillable"
            checked={isBillable}
            onChange={(e) => setIsBillable(e.target.checked)}
            className="h-4 w-4 md:h-5 md:w-5 text-blue-600 focus:ring-blue-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800 border-gray-300 dark:border-gray-600 rounded-lg cursor-pointer"
          />
          <label htmlFor="isBillable" className="flex-1 text-xs md:text-sm font-medium text-gray-900 dark:text-gray-100 cursor-pointer">
            💰 {t('timeTracking.billableTime')}
          </label>
        </div>

        {/* Gombok */}
        <div className="flex flex-col sm:flex-row justify-end gap-2 md:gap-3 pt-3 md:pt-6 border-t-2 border-gray-100 dark:border-gray-700">
          <button
            type="button"
            onClick={onClose}
            disabled={loading}
            className="px-4 md:px-6 py-2 md:py-3 text-gray-700 dark:text-gray-300 font-medium hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg md:rounded-xl transition-all text-sm md:text-base order-2 sm:order-1"
          >
            {t('timeTracking.cancel')}
          </button>
          <button
            type="submit"
            disabled={loading}
            className="px-4 md:px-6 py-2 md:py-3 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 dark:from-blue-500 dark:to-indigo-500 dark:hover:from-blue-600 dark:hover:to-indigo-600 text-white font-semibold rounded-lg md:rounded-xl shadow-lg hover:shadow-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed md:transform md:hover:scale-105 text-sm md:text-base order-1 sm:order-2"
          >
            {loading ? `⏳ ${t('timeTracking.saving')}` : initialData ? `✏️ ${t('timeTracking.update')}` : `✨ ${t('timeTracking.create')}`}
          </button>
        </div>
      </form>
    </Modal>
  );
}
